import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { LayoutGrid, List as ListIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { truthy, attrLabel, attrValue } from './helpers';

export const OverviewTab: React.FC<{
  description?: string;
  attributesLoading: boolean;
  attributes: any[];
}> = ({ description, attributesLoading, attributes }) => {
  const [attrView, setAttrView] = useState<'list' | 'grid'>('list');

  // Deleted/inactive rows are moderation noise, not spec data — hide them and
  // sort by label so the sheet reads like a scannable product spec.
  const visibleAttributes = useMemo(
    () =>
      attributes
        .filter((a) => !truthy(a.deleted) && a.active !== 0)
        .sort((a, b) => attrLabel(a).localeCompare(attrLabel(b))),
    [attributes]
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Description</h3>
        {description ? (
          <div className="text-sm leading-relaxed text-gray-600 [&_a]:text-indigo-600 [&_a]:underline [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_h1]:mt-4 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-gray-900 [&_h2]:mt-4 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-gray-900 [&_li]:mb-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_strong]:text-gray-800 [&_ul]:list-disc [&_ul]:pl-5">
            <ReactMarkdown>{description}</ReactMarkdown>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No description provided.</p>
        )}
      </div>
      <div>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Attributes</h3>
            {visibleAttributes.length > 0 && (
              <span className="text-xs text-gray-400">{visibleAttributes.length} specs</span>
            )}
          </div>
          {visibleAttributes.length > 0 && (
            <div className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5">
              <button
                onClick={() => setAttrView('list')}
                className={`rounded p-1 transition-colors ${
                  attrView === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-600'
                }`}
                title="List view"
              >
                <ListIcon className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setAttrView('grid')}
                className={`rounded p-1 transition-colors ${
                  attrView === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Grid view"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {attributesLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : visibleAttributes.length === 0 ? (
          <p className="text-sm text-gray-400">No attributes recorded.</p>
        ) : attrView === 'list' ? (
          <dl className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-100">
            {visibleAttributes.map((a: any, i: number) => (
              <div
                key={a.eaid ?? i}
                className="flex items-center justify-between gap-3 px-4 py-2.5 odd:bg-gray-50/50"
              >
                <dt className="shrink-0 text-sm text-gray-500">{attrLabel(a)}</dt>
                <dd
                  className="max-w-[65%] truncate text-right text-sm font-medium text-gray-900"
                  title={attrValue(a)}
                >
                  {attrValue(a)}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {visibleAttributes.map((a: any, i: number) => (
              <div
                key={a.eaid ?? i}
                className="rounded-xl border border-gray-100 bg-gray-50/50 p-3"
              >
                <dt className="truncate text-[11px] uppercase tracking-wide text-gray-400" title={attrLabel(a)}>
                  {attrLabel(a)}
                </dt>
                <dd className="mt-0.5 break-words text-sm font-semibold text-gray-900">
                  {attrValue(a)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
};
